/**
 * WheelAdapter - Handles trackpad/wheel gestures for the GestureController
 *
 * Key features:
 * - Exponential dampening for natural feel
 * - Idle timeout to detect gesture end (works for both trackpads and discrete wheels)
 * - Cooldown to prevent immediate restart after snap-back
 */
export class WheelAdapter {
  constructor(controller, options = {}) {
    this.controller = controller;
    this.element = controller.element;

    // Configuration
    this.sensitivity = options.sensitivity ?? 1.5;
    this.maxDelta = options.maxDelta ?? 600;
    this.settlementDelay = options.settlementDelay ?? 150;

    // Internal state
    this.deltaX = 0;
    this.deltaY = 0;
    this.direction = null;
    this.active = false;
    this.lastTime = 0;
    this.checkInterval = null;
    this.cooldown = false;

    this.handleWheel = this.handleWheel.bind(this);
    this.element.addEventListener('wheel', this.handleWheel, { passive: false });
  }

  /**
   * Exponential clamping - applies increasing resistance as value approaches max
   * Uses tanh for smooth S-curve dampening
   */
  clampExponential(value, maxValue) {
    return maxValue * Math.tanh(value / maxValue);
  }

  /**
   * Apply diminishing returns as we get further from origin
   */
  dampenDelta(current, delta, max) {
    const absVal = Math.abs(current);
    const factor = Math.max(0.1, 1 - (absVal / max) * 0.8);
    return delta * factor;
  }

  handleWheel(e) {
    if (this.controller.isLocked() || this.cooldown) return;

    // If the target is inside a nested scrollable element, let the browser handle it
    if (!this.active) {
      const scrollable = this.controller.isNestedScrollable(e.target);
      if (scrollable) {
        const canScrollY = scrollable.scrollHeight > scrollable.clientHeight;
        const canScrollX = scrollable.scrollWidth > scrollable.clientWidth;

        if (canScrollY || canScrollX) {
          const atTop = scrollable.scrollTop <= 0;
          const atBottom = scrollable.scrollTop >= scrollable.scrollHeight - scrollable.clientHeight - 1;
          const atLeft = scrollable.scrollLeft <= 0;
          const atRight = scrollable.scrollLeft >= scrollable.scrollWidth - scrollable.clientWidth - 1;

          // For vertical scroll: allow native scroll unless at edge and scrolling further
          if (canScrollY && Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
            const scrollingUp = e.deltaY < 0;
            const scrollingDown = e.deltaY > 0;
            if (!(atTop && scrollingUp) && !(atBottom && scrollingDown)) {
              return; // Let browser scroll the nested element
            }
          }

          // For horizontal scroll: allow native scroll unless at edge
          if (canScrollX && Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
            const scrollingLeft = e.deltaX < 0;
            const scrollingRight = e.deltaX > 0;
            if (!(atLeft && scrollingLeft) && !(atRight && scrollingRight)) {
              return; // Let browser scroll the nested element
            }
          }
        }
      }
    }

    e.preventDefault();

    const now = performance.now();
    this.lastTime = now;

    // Scale and invert deltas for natural scrolling
    const dx = -e.deltaX * this.sensitivity;
    const dy = -e.deltaY * this.sensitivity;

    // Start gesture
    if (!this.active) {
      this.active = true;
      this.deltaX = 0;
      this.deltaY = 0;
      this.direction = null;
      this.controller.handleStart(0, 0, e);
    }

    // Accumulate with dampening
    this.deltaX += this.dampenDelta(this.deltaX, dx, this.maxDelta);
    this.deltaY += this.dampenDelta(this.deltaY, dy, this.maxDelta);

    // Determine direction once (after 10px movement)
    if (!this.direction && (Math.abs(this.deltaX) > 10 || Math.abs(this.deltaY) > 10)) {
      this.direction = Math.abs(this.deltaX) > Math.abs(this.deltaY) ? 'h' : 'v';
      // Tell controller to lock direction
      this.controller.lockDirection(this.direction);
    }

    // Apply clamped values for visual output
    const clampedX = this.clampExponential(this.deltaX, this.maxDelta);
    const clampedY = this.clampExponential(this.deltaY, this.maxDelta);

    // Send clamped move to controller
    this.controller.handleMove(clampedX, clampedY, e, true /* isWheel */);

    // Start checking for release
    this.startSettlementCheck();
  }

  startSettlementCheck() {
    if (this.checkInterval) return;

    this.checkInterval = setInterval(() => {
      const timeSinceLastWheel = performance.now() - this.lastTime;

      if (timeSinceLastWheel > this.settlementDelay && this.active) {
        this.endGesture();
      }
    }, 50);
  }

  endGesture() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }

    if (!this.active) return;

    this.active = false;
    this.controller.handleEnd(null, {
      deltaX: this.deltaX,
      deltaY: this.deltaY,
      direction: this.direction,
    });

    // Reset state
    this.deltaX = 0;
    this.deltaY = 0;
    this.direction = null;

    // Brief cooldown to prevent immediate restart
    this.cooldown = true;
    setTimeout(() => {
      this.cooldown = false;
    }, 100);
  }

  destroy() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }
    this.element.removeEventListener('wheel', this.handleWheel);
  }
}
